<script lang="ts">
	// Stores
		import { getDeviceStatus, getDeviceSettings } from 'src/stores/device-runtime/status-selectors';

	// Services and configs
	import { devices_config } from 'src/configs/devices';
	import { getDeviceChartsConfig } from 'src/helpers/statistics/config/device_chart_config';

	// Constants
	import { GAS_SENSOR_TYPES } from 'src/constants';

	// Helpers
	import { checkIsCriticalEvent } from 'src/helpers/device_data_helpers';
	import { capFirstChar } from 'src/helpers/utilities';
	import {
		extractGen1MainLabel,
		getGen1DeviceStatuses,
		createGen1StatusLabels,
	} from 'src/components/Sidepanel/SidePanelLayoutProvider/SidePanelDeviceLayout/Devices/Shelly/Gen1/gen1-main-helpers';
	import {
		getSidepanelStatusTitle,
		getSidepanelStatusValue,
		resolveBluetoothSensorStatusSlots,
	} from 'src/helpers/sidepanel_status_slots';

	// Design System Components
	import { CircleAvatar, ParameterCardV2, Text } from '@Shelly/shelly-design-system';
	// Helpers
	import { mapIconToThin } from 'src/helpers/new-layout-helpers';

	// Components
	import DeviceViewStateWrapper from 'src/components/Sidepanel/DeviceViews/DeviceViewStateWrapper.svelte';
	import ControlGasSensorMute from 'src/components/Controls/Single/ControlGasSensorMute.svelte';
	import DeviceNotificationLabel from 'src/components/Sidepanel/DeviceNotificationLabel.svelte';
	import { useNavigateToHistoryStatisticsTab } from 'src/components/Sidepanel/useNavigateToHistoryStatisticsTab';
	import SidepanelStatusPeek from 'src/components/Sidepanel/SidepanelStatusPeek.svelte';

	// Types
	import type { Device } from 'src/types/Device.ts';

	import DeviceLabelList from '../DeviceLabelList.svelte';

	/**
	 * Main control tab for the sensor devices sidepanel drawer.
	 *
	 * Responsibilities:
	 * - centered avatar for the primary status
	 * - H&T humidity as HeroValue above PeekTray; extras in the tray
	 * - firmware incompatibility warnings
	 *
	 * @param {Device} device - The device for which the information is displayed.
	 */
	export let device: Device;
	const { navigateToHistoryStatisticsTab } = useNavigateToHistoryStatisticsTab();

	$: isGasSensor = GAS_SENSOR_TYPES.includes(device.type as (typeof GAS_SENSOR_TYPES)[number]);

	$: deviceConfig = devices_config?.[device?.type];

	$: statusStore = getDeviceStatus(device?.id);
	$: status = $statusStore;
	$: settingsStore = getDeviceSettings(device?.id);
	$: settings = $settingsStore;
	$: deviceChartsConfig = device ? getDeviceChartsConfig(device) : [];

	$: _statuses = getGen1DeviceStatuses(status, device, settings, deviceConfig);
	$: labelList = createGen1StatusLabels(_statuses);

	$: mainLabel = extractGen1MainLabel(_statuses);
	$: statusSlots = resolveBluetoothSensorStatusSlots(device, _statuses);
	$: avatarStatus = statusSlots.avatar;
	$: heroStatus = statusSlots.hero;
	$: showPeek = statusSlots.peek.length > 0;
	$: showFloating = !!heroStatus || showPeek;
	$: usePeekLayout = !!avatarStatus || showFloating;

	const handleMainLabelClick = () => {
		if (deviceChartsConfig.length) {
			navigateToHistoryStatisticsTab();
		}
	};
</script>

<DeviceViewStateWrapper
	{device}
	hideSlotStaticBottom={usePeekLayout && !isGasSensor}
	hideSlotFloatingBottom={!showFloating}
>
	<div slot="staticTop">
		{#if !usePeekLayout}
			<DeviceLabelList {labelList} />
		{/if}
		<DeviceNotificationLabel deviceId={device?.id} />
	</div>

	<svelte:fragment slot="content">
		{#if avatarStatus}
			<div class="flex-full-height align-items-center justify-content-center">
				<div
					class="d-flex flex-direction-column align-items-center gap-s"
					role="img"
					aria-label="{getSidepanelStatusTitle(avatarStatus)} {getSidepanelStatusValue(avatarStatus)}"
				>
					<CircleAvatar
						size="xxl"
						icon={mapIconToThin(avatarStatus.icon)}
						backgroundColor="color-0"
					/>
					<Text styleType="h1">{capFirstChar(getSidepanelStatusValue(avatarStatus))}</Text>
					<Text styleType="miniature" class="text-color-muted">
						{getSidepanelStatusTitle(avatarStatus)}
					</Text>
				</div>
			</div>
		{/if}
	</svelte:fragment>

	<svelte:fragment slot="floatingBottom">
		{#if showFloating}
			<SidepanelStatusPeek
				showMain={!!heroStatus}
				main={heroStatus}
				peek={statusSlots.peek}
			/>
		{/if}
	</svelte:fragment>

	<div class="flex-full-height justify-content-end" slot="staticBottom">
		<div>
			{#if !usePeekLayout && mainLabel}
				{@const isCriticalEvent = checkIsCriticalEvent(mainLabel.value)}
				<ParameterCardV2
					class={deviceChartsConfig.length > 0 ? 'mb-s cursor-pointer' : 'mb-s'}
					icon={mapIconToThin(mainLabel.icon)}
					title={capFirstChar(mainLabel.value)}
					description={capFirstChar(mainLabel.translation)}
					backgroundColor={isCriticalEvent ? '--color-danger-10' : '--color-0'}
					isFocusable={deviceChartsConfig.length > 0}
					on:click={handleMainLabelClick}
				/>
			{/if}
		</div>
		{#if isGasSensor}
			<ControlGasSensorMute {device} />
		{/if}
	</div>
</DeviceViewStateWrapper>
